import type { Passenger } from '@fz/ui-components-react';
import type { BookingResponse } from '@models/landing/api/api-response.model';
import type { OffloadPaxItem, OffloadPaxRequest } from '@models/landing/api/offload-pax.model';
import { PassengerTypeEnum as ApiPassengerTypeCode } from '@models/landing/master-data/master-data.model';
import { PassengerTypeEnum } from '@models/landing/ui/select-passenger-model';
import type { CheckedInPax, passengerContactDetails } from '@models/ui/pnr-data-model';
import { getPassengerTypeLabel } from '@helper/passenger-table-view.mapper';
import type { Translate } from '@utils/flight-util';

const CHECKED_IN_STATUS_KEY = 'SelfCheckout.PassengerCheckedin.Text';

/**
 * Maps confirm checked-in passengers into PassengersTable rows
 * for the Cancel check-in ActionPopup (name + status).
 */
export const mapCheckedInPaxToCancelCheckInTable = (
  checkedInPaxList: CheckedInPax[],
  t: Translate,
): Passenger[] =>
  checkedInPaxList.map((pax) => {
    const isInfant = pax.passengerType === PassengerTypeEnum.Infant;
    const typeLabel = getPassengerTypeLabel(pax.passengerType);
    const nameWithType = typeLabel ? `${pax.fullName} (${typeLabel})` : pax.fullName;

    return {
      id: pax.paxId,
      name: nameWithType,
      type: typeLabel,
      isInfant,
      associatedInfantID: pax.associatedInfantID ?? '',
      flights: [],
      checkInStatus: t(CHECKED_IN_STATUS_KEY),
    };
  });

/**
 * Returns the selected passenger ID plus any linked infant/parent IDs.
 */
export const getLinkedCancelCheckInPaxIds = (
  passengerId: string,
  checkedInPaxList: CheckedInPax[],
): string[] => {
  const linkedIds = new Set<string>([passengerId]);
  const selectedPax = checkedInPaxList.find((pax) => pax.paxId === passengerId);

  if (selectedPax?.associatedInfantID) {
    linkedIds.add(selectedPax.associatedInfantID);
  }

  checkedInPaxList.forEach((pax) => {
    if (pax.associatedInfantID === passengerId) {
      linkedIds.add(pax.paxId);
    }
  });

  return [...linkedIds];
};

/**
 * Applies select/deselect to a passenger and their linked infant/parent.
 */
export const resolveCancelCheckInSelection = (
  passengerId: string,
  isSelected: boolean,
  previousSelectedIds: string[],
  checkedInPaxList: CheckedInPax[],
): string[] => {
  const linkedIds = getLinkedCancelCheckInPaxIds(passengerId, checkedInPaxList);

  if (isSelected) {
    return [...new Set([...previousSelectedIds, ...linkedIds])];
  }

  return previousSelectedIds.filter((id) => !linkedIds.includes(id));
};

const toOffloadPaxItem = (contact: passengerContactDetails): OffloadPaxItem | null => {
  if (contact.resPaxId === undefined || !contact.paxJourneyId) {
    return null;
  }

  return {
    paxJourneyId: contact.paxJourneyId,
    resPaxId: contact.resPaxId,
    recordNumber: contact.recordNumber ?? 0,
    sprintRefId: contact.resPaxId,
    parentID: contact.parentID ?? null,
  };
};

/**
 * Builds `/Passenger/Offload` request body from selected confirmation passenger IDs.
 */
export const buildOffloadPaxRequest = (
  selectedPaxIds: string[],
  offloadPaxList: passengerContactDetails[],
): OffloadPaxRequest => {
  const selectedIdSet = new Set(selectedPaxIds);

  const paxList = offloadPaxList
    .filter((contact) => contact.paxId !== undefined && selectedIdSet.has(contact.paxId))
    .map(toOffloadPaxItem)
    .filter((item): item is OffloadPaxItem => item !== null);

  return { paxList };
};

const resolveOffloadParentId = (pax: BookingResponse['paxList'][number]): number | null => {
  if (pax.passengerType !== ApiPassengerTypeCode.Infant) {
    return null;
  }

  return pax.travelsWithPassengerId ?? pax.parentID ?? null;
};

/**
 * Maps checked-in passengers from a booking response into offload payload contacts.
 * Shared by landing (retrieve) and confirmation (confirm) flows.
 */
export const mapOffloadPaxListFromBooking = (
  response: BookingResponse,
  checkedInPaxList: CheckedInPax[],
): passengerContactDetails[] => {
  if (!response.paxList?.length) {
    return [];
  }

  const checkedInPaxIds = new Set(checkedInPaxList.map((pax) => pax.paxId));

  return response.paxList
    .filter((pax) => checkedInPaxIds.has(String(pax.resPaxId)))
    .map((pax) => ({
      paxId: String(pax.resPaxId),
      resPaxId: pax.resPaxId,
      recordNumber: pax.recordNumber,
      paxJourneyId: pax.paxJourneyId,
      parentID: resolveOffloadParentId(pax),
      email: '',
      phone: '',
      countryCode: '',
      firstName: pax.firstName ?? '',
      lastName: pax.lastName ?? '',
      passengerType: '',
    }));
};
